/**
 * ImagePreloadService - Singleton class for preloading critical images
 *
 * Mirrors AudioService architecture for consistent preloading strategy.
 * Preloads critical app images before React renders to prevent:
 * - Flash of unstyled content (FOUC)
 * - Progressive top-to-bottom image loading
 * - Layout shifts during initial render
 *
 * Uses Image() constructor for browser-native preloading with progress tracking.
 */

// Import critical images from centralized assets
import {
  backgroundImage,
  topLeftBg,
  topRightBg,
  bottomLeftBg,
  bottomRightBg,
  ludokaLogo,
  yasGreenLogo,
  yasFlag,
  coinImg as coinIcon,
  settingIcon
} from '@/assets';

/**
 * Critical images that must load before app initialization
 * These are visible on every page or the landing page (most common route)
 */
const CRITICAL_IMAGES = [
  // App.tsx backgrounds (visible on all pages)
  backgroundImage,
  topLeftBg,
  topRightBg,
  bottomLeftBg,
  bottomRightBg,
  // Landing page essentials
  ludokaLogo,
  yasGreenLogo,
  yasFlag,
  coinIcon,
  settingIcon
] as const;

/**
 * Total number of critical images to preload
 */
const TOTAL_CRITICAL_IMAGES = CRITICAL_IMAGES.length;

/**
 * ImagePreloadService - Singleton for managing image preloading
 *
 * Architecture mirrors AudioService for consistency:
 * - Singleton pattern with getInstance()
 * - Progress tracking (loadedCount / totalAssets)
 * - Callback system (onReady, onProgress)
 * - Ready state flag (isReady)
 */
class ImagePreloadService {
  private static instance: ImagePreloadService | null = null;

  private loadedCount = 0;
  private totalAssets = TOTAL_CRITICAL_IMAGES;
  private _isReady = false;
  private onReadyCallbacks: (() => void)[] = [];
  private onProgressCallbacks: ((progress: number) => void)[] = [];

  private constructor() {
    // Start preloading immediately on instantiation
    this.preloadCriticalImages();
  }

  /**
   * Get the singleton instance of ImagePreloadService
   */
  public static getInstance(): ImagePreloadService {
    ImagePreloadService.instance ??= new ImagePreloadService();
    return ImagePreloadService.instance;
  }

  /**
   * Preload all critical images using Image() constructor
   */
  private preloadCriticalImages(): void {
    CRITICAL_IMAGES.forEach((src) => {
      const img = new Image();

      // Handle successful load
      img.onload = () => {
        this.handleImageLoaded();
      };

      // Handle load error (still count as "loaded" to prevent blocking)
      img.onerror = () => {
        if (import.meta.env.DEV) {
          console.warn('[ImagePreloadService] Failed to load:', src);
        }
        this.handleImageLoaded();
      };

      // Trigger browser fetch
      img.src = src;
    });
  }

  /**
   * Calculate current loading progress (0-100)
   */
  private calculateProgress(): number {
    return Math.round((this.loadedCount / this.totalAssets) * 100);
  }

  /**
   * Handle individual image load completion
   * Mirrors AudioService.handleAssetLoaded() pattern
   */
  private handleImageLoaded(): void {
    // Increment loaded count
    this.loadedCount++;

    // Calculate progress percentage (0-100)
    const progress = this.calculateProgress();

    // Notify progress callbacks
    this.onProgressCallbacks.forEach((cb) => {
      cb(progress);
    });

    // Check if all assets loaded
    if (this.loadedCount >= this.totalAssets) {
      this._isReady = true;

      // Notify ready callbacks
      this.onReadyCallbacks.forEach((cb) => {
        cb();
      });

      // Clear callbacks after execution to prevent memory leaks
      this.onReadyCallbacks = [];
      this.onProgressCallbacks = [];
    }
  }

  /**
   * Register a callback to be called when all critical images are loaded
   * Mirrors AudioService.onReady() pattern
   */
  public onReady(callback: () => void): void {
    if (this._isReady) {
      // Already ready - execute immediately
      callback();
    } else {
      // Not ready - queue callback
      this.onReadyCallbacks.push(callback);
    }
  }

  /**
   * Register a callback for progress updates (0-100)
   */
  public onProgress(callback: (progress: number) => void): void {
    if (this._isReady) {
      // Already ready - report 100% immediately
      callback(100);
    } else {
      // Not ready - queue callback and report current progress
      this.onProgressCallbacks.push(callback);
      callback(this.calculateProgress());
    }
  }

  /**
   * Returns whether all critical images are preloaded and ready
   */
  public get isReady(): boolean {
    return this._isReady;
  }

  /**
   * Get current loading progress (0-100)
   */
  public get progress(): number {
    return this.calculateProgress();
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  public static resetInstance(): void {
    if (ImagePreloadService.instance) {
      ImagePreloadService.instance.loadedCount = 0;
      ImagePreloadService.instance._isReady = false;
      ImagePreloadService.instance.onReadyCallbacks = [];
      ImagePreloadService.instance.onProgressCallbacks = [];
      ImagePreloadService.instance = null;
    }
  }
}

// Export singleton getter
export const getImagePreloadService = ImagePreloadService.getInstance.bind(ImagePreloadService);

// Export class for testing purposes
export { ImagePreloadService };
